import React, { useState, useEffect, useCallback } from "react";
import '../../assets/css/Restaurants.css';
import { CSVLink } from "react-csv";
import { jsPDF } from 'jspdf';
import * as XLSX from 'xlsx';
import LoadingSpinner from "../../utils/LoadingSpinner";
import { useTranslation } from 'react-i18next';
import { loadPaginatedLocations } from "../../utils/Utils";
import { getAnnouncements } from "../../utils/AWSAPI";
import i18n from "../Translations/i18n/i18n";

export default function Restaurants() {
  const { t } = useTranslation();

  // DATA API
  const [isLoading, setLoading] = useState(true);
  const [data, setData] = useState([]);
  const [announcements, setAnnouncements] = useState(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPrevPage, setHasPrevPage] = useState(false);
  const itemsPerPage = 10;

  const [searchInput, setSearchInput] = useState("");
  const [filteredData, setFilteredData] = useState([]);

  const [sortColumn, setSortColumn] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");

  const [expandedRestaurants, setExpandedRestaurants] = useState({});

  const [isModalShowing, setModalShowing] = useState(false);
  const [isModalUncloseable, setModalUncloseable] = useState(false);
  const [modalHeader, setModalHeader] = useState(t('Loading...'));
  const [modalBody, setModalBody] = useState(<></>);
  const [modalFooter, setModalFooter] = useState(<></>);

  const closeModal = () => setModalShowing(false);

  const Modal = () => (
    <form>
      <div className="modal show">
        <div className="modal-content">
          <div className="modal-header">
            <h4>{modalHeader}</h4>
            {!isModalUncloseable && (
              <span className="close" onClick={closeModal}>&times;</span>
            )}
          </div>
          <div className="modal-body">{modalBody}</div>
          <div className="modal-footer">{modalFooter}</div>
        </div>
      </div>
    </form>
  );

  const updateModal = (header, body, footer = <></>) => {
    setModalShowing(!isModalShowing);
    setModalHeader(header);
    setModalBody(body);
    setModalFooter(footer);
  };

  useEffect(() => {
    const language = localStorage.getItem("language");
    if (language) {
      i18n.changeLanguage(language);
    }
  }, []);

  const fetchData = useCallback(async (page) => {
    setLoading(true);
    try {
      const offset = (page - 1) * itemsPerPage;
      const limit = offset + itemsPerPage + 1;
            
      const result = await loadPaginatedLocations("True", localStorage.getItem("user_identifier"), offset, limit);

      if (result.Error) {
        setModalUncloseable(false);
        updateModal("Error", t("Couldn't fetch locations.  Error: " + result.message));
        setLoading(false);
        return;
      }

      const items = result.data?.Items || [];      
      const hasMore = items.length > itemsPerPage;
      const displayItems = hasMore ? items.slice(0, itemsPerPage) : items;
            
      setData(displayItems);
      setFilteredData(displayItems);
      setHasNextPage(hasMore);
      setHasPrevPage(page > 1);
      setCurrentPage(page);

      if (announcements === null) {
        const announcementsData = await getAnnouncements();
        setAnnouncements(announcementsData || []);
      }
    } catch (err) {
      console.log(err);
      updateModal("Error", t("An error occurred while fetching data."));
    } finally {
      setLoading(false);
    }
  }, [announcements, t]);

  useEffect(() => {
    fetchData(1);
  }, []);

  useEffect(() => {
    if (!searchInput.trim()) {
      setFilteredData(sortData(data));
      return;
    }

    const searchLower = searchInput.toLowerCase();
    const filtered = data.filter(item =>
      Object.values(item).some(value => 
        value?.S?.toString().toLowerCase().includes(searchLower) ||
        value?.N?.toString().includes(searchLower)
      )
    );
    setFilteredData(sortData(filtered));
  }, [searchInput, data, sortColumn, sortOrder]);

  const sortData = useCallback((dataToSort) => {
    if (!sortColumn || !dataToSort.length) return dataToSort;

    return [...dataToSort].sort((a, b) => {
      let valA, valB;

      if (sortColumn === "MCName") {
        const numA = parseInt(a.MCName?.S?.replace(/\D/g, "") || "0");
        const numB = parseInt(b.MCName?.S?.replace(/\D/g, "") || "0");
        valA = numA;
        valB = numB;
      } else if (["TotalIncidents", "CriticalIncidents"].includes(sortColumn)) {
        valA = parseInt(a[sortColumn]?.N || "0");
        valB = parseInt(b[sortColumn]?.N || "0");
      } else {
        valA = a[sortColumn]?.S || "";
        valB = b[sortColumn]?.S || "";
      }

      if (typeof valA === "number" && typeof valB === "number") {
        return sortOrder === "asc" ? valA - valB : valB - valA;
      }
      
      return sortOrder === "asc" 
        ? String(valA).localeCompare(String(valB))
        : String(valB).localeCompare(String(valA));
    });
  }, [sortColumn, sortOrder]);

  useEffect(() => {
    setFilteredData(prev => sortData([...prev]));
  }, [sortColumn, sortOrder, sortData]);

  const handleNextPage = () => {
    if (hasNextPage) {
      fetchData(currentPage + 1);
      setSearchInput("");
    }
  };

  const handlePrevPage = () => {
    if (hasPrevPage) {
      fetchData(currentPage - 1);
      setSearchInput("");
    }
  };

  const handleFirstPage = () => {
    if (currentPage !== 1) {
      fetchData(1);
      setSearchInput("");
    }
  };

  const toggleDropdown = (restaurantID) => {
    setExpandedRestaurants((prev) => ({
      ...prev,
      [restaurantID]: !prev[restaurantID],
    }));
  };

  const handleCopyToClipboard = () => {
    const tempTable = document.createElement('table');
    const tempBody = tempTable.createTBody();

    filteredData.forEach(({ MCName, TotalIncidents, Address, City, Province, CriticalIncidents }) => {
      const row = tempBody.insertRow();
      row.insertCell().textContent = MCName?.S || '';
      row.insertCell().textContent = Address?.S || '';
      if (localStorage.getItem("user_power_user") === "true") {
        row.insertCell().textContent = City?.S || '';
        row.insertCell().textContent = Province?.S || '';
      }
      row.insertCell().textContent = TotalIncidents?.N || '';
      row.insertCell().textContent = CriticalIncidents?.N || '';
    });

    document.body.appendChild(tempTable);
    const range = document.createRange();
    range.selectNodeContents(tempTable);
    window.getSelection().addRange(range);
    document.execCommand('copy');
    window.getSelection().removeAllRanges();
    document.body.removeChild(tempTable);

    alert(t('Restaurants have been copied to your clipboard.'));
  };

  const handleExportExcel = () => {
    const table = document.getElementById('table-to-export');
    const ws = XLSX.utils.table_to_sheet(table);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');
    XLSX.writeFile(wb, 'Restaurants.xlsx');
  };

  const handleExportPDF = () => {
    const table = document.getElementById('table-to-export');
    const pdf = new jsPDF('p', 'pt', 'letter');
    pdf.html(table, { html2canvas: { scale: 0.45 } }).then(() => {
      pdf.save('Restaurants.pdf');
    });
  };

  const transformDataForCSV = (items) => {
    return items.map((item) => {
      const transformedItem = {};
      for (const key in item) {
        if (Object.hasOwnProperty.call(item, key)) {
          transformedItem[key] = item[key].S || item[key].N || '';
        }
      }
      return transformedItem;
    });
  };

  return (
    <div className="restaurant">
      <LoadingSpinner show={isLoading} />

      {isModalShowing && <Modal />}

      {!isLoading && (
        <div className="recuadro">
          {announcements?.length > 0 ? (
            <a href="/announcements" target="_blank" style={{ textDecoration: 'underline' }}>
              <div style={{ backgroundColor: 'rgb(238, 208, 38)', padding: '10px', color: 'black', fontWeight: 'bold' }}>
                {t('ActiveAnnouncements')}
              </div>
            </a>
          ) : (
            <div style={{ backgroundColor: 'rgb(238, 208, 38)', padding: '10px', color: 'black', fontWeight: 'bold' }}>
              {t('NoAnnouncements')}
            </div>
          )}

          <div className="menu-buttons">
            <button className="save-as-bottom" onClick={handleCopyToClipboard}>{t('Copy')}</button>
            <button className="save-as-bottom" onClick={handleExportExcel}>{t('Excel')}</button>
            {filteredData && filteredData.length > 0 && (
              <button className="save-as-bottom">
                <CSVLink
                  data={transformDataForCSV(filteredData)}
                  filename="Restaurants.csv"
                  target="_blank"
                  className="csv"
                >
                  {t('CSV')}
                </CSVLink>
              </button>
            )}
            <button className="save-as-bottom" onClick={handleExportPDF}>{t('PDF')}</button>

            <select
              value={sortColumn}
              onChange={(e) => setSortColumn(e.target.value)}
              className="sort-select save-as-bottom"
            >
              <option value="">{t('Sort By')}</option>
              <option value="MCName">{t('Restaurant')}</option>
              <option value="Address">{t('Address')}</option>
              {localStorage.getItem("user_power_user") === "true" && (
                <>
                  <option value="City">{t('City')}</option>
                  <option value="Province">{t('Province')}</option>
                </>
              )}
              <option value="TotalIncidents">{t('Total Incidents')}</option>
              <option value="CriticalIncidents">{t('Critical Incidents')}</option>
            </select>

            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              className="sort-select save-as-bottom"
            >
              <option value="asc">{t('Ascending')}</option>
              <option value="desc">{t('Descending')}</option>
            </select>

            <input
              type="text"
              placeholder={t('Search current page...')}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="save-as-bottom"
            />
          </div>

          <div className="table-container">
            <table id="table-to-export" className="Rest">
              <thead>
                <tr className="red">
                  <th>{t('Restaurant')}</th>
                  <th>{t('Total Incidents')}</th>
                  <th>{t('Critical Incidents')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredData.length === 0 ? (
                  <tr>
                    <td colSpan="3" style={{ textAlign: 'center', padding: '20px' }}>
                      {searchInput ? t('No matches found') : t('No restaurants found')}
                    </td>
                  </tr>
                ) : (
                  filteredData.map(({ MCName, MCID, TotalIncidents, Address, City, Province, CriticalIncidents }) => (
                    <React.Fragment key={MCID?.S}>
                      <tr>
                        <td>
                          <button onClick={() => toggleDropdown(MCID?.S)} className="toggle-button">
                            {expandedRestaurants[MCID?.S] ? '−' : '＋'}
                          </button>
                          <a className="hov" href={`dashboard?location=${MCID?.S}&locationName=${MCName?.S}`}>
                            {MCName?.S}
                          </a>
                        </td>
                        <td>{TotalIncidents?.N}</td>
                        <td>{CriticalIncidents?.N}</td>
                      </tr>
                      {expandedRestaurants[MCID?.S] && (
                        <tr>
                          <td colSpan="3" className="dropdown">
                            <div><strong>{t('Address')}:</strong> {Address?.S}</div>
                            <div><strong>{t('City')}:</strong> {City?.S}</div>
                            <div><strong>{t('Province')}:</strong> {Province?.S}</div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="pagination" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', padding: '20px' }}>
            <button onClick={handleFirstPage} disabled={!hasPrevPage} style={{ padding: '8px 16px' }}>
              «
            </button>
            <button onClick={handlePrevPage} disabled={!hasPrevPage} style={{ padding: '8px 16px' }}>
              ‹
            </button>
            
            <span style={{ padding: '8px 16px', backgroundColor: '#DA291C', color: 'white', minWidth: '10px', textAlign: 'center'}}>
              {currentPage}
            </span>
            
            <button onClick={handleNextPage} disabled={!hasNextPage} style={{ padding: '8px 16px' }}>
              ›
            </button>
          </div>
        </div>
      )}
    </div>
  );
}